import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { Box } from "../../Box/Box.mjs";
import { normalizeKey } from "../normalizeKey.mjs";
import { Heading } from "../../Heading/Heading.mjs";
import { Stack } from "../../Stack/Stack.mjs";
import { Columns } from "../../Columns/Columns.mjs";
import { Column } from "../../Column/Column.mjs";
import { Overlay } from "../Overlay/Overlay.mjs";
import { Bleed } from "../../Bleed/Bleed.mjs";
import "../../Text/TextContext.mjs";
import "../../Heading/HeadingContext.mjs";
import "../../../css/lineHeightContainer.css.mjs";
import { buildDataAttributes } from "../buildDataAttributes.mjs";
import "../../../css/reset/reset.css.mjs";
import "../../../css/atoms/sprinkles.css.mjs";
import "../../../hooks/useIcon/icon.css.mjs";
import "../../../css/typography.css.mjs";
import { IconClose } from "../../icons/IconClose/IconClose.mjs";
import { ButtonIcon } from "../../ButtonIcon/ButtonIcon.mjs";
import { headingRoot, headingFocus, pointerEventsAll, maxSize, closeIconOffset } from "./Modal.css.mjs";
import { Inline } from "../../Inline/Inline.mjs";
const modalPadding = ["gutter", "large"];
const ModalContentHeader = forwardRef(
  ({ title, headingLevel, description, descriptionId, center, headerIllustration, actionButton }, ref) => /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    actionButton && /* @__PURE__ */ jsx(
      Box,
      {
        zIndex: 2,
        position: headerIllustration ? "absolute" : void 0,
        background: headerIllustration ? "customDark" : void 0,
        children: /* @__PURE__ */ jsx(Inline, { space: "none", children: actionButton })
      }
    ),
    /* @__PURE__ */ jsx(Heading, { level: headingLevel, align: center ? "center" : void 0, children: /* @__PURE__ */ jsxs(
      Box,
      {
        ref,
        component: "span",
        tabIndex: -1,
        outline: "none",
        position: "relative",
        className: headingRoot,
        children: [
          title,
          /* @__PURE__ */ jsx(
            Overlay,
            {
              boxShadow: "outlineFocus",
              borderRadius: "xlarge",
              transition: "fast",
              className: headingFocus,
              onlyVisibleForKeyboardNavigation: true
            }
          )
        ]
      }
    ) }),
    description ? /* @__PURE__ */ jsx(Box, { id: descriptionId, children: description }) : null,
    headerIllustration ? /* @__PURE__ */ jsx(Bleed, { horizontal: { mobile: "gutter", tablet: "large" }, top: "xxxxlarge", children: headerIllustration }) : null
  ] })
);
ModalContentHeader.displayName = "ModalContentHeader";
const ModalContent = ({
  id,
  children,
  description,
  onClose,
  width,
  closeLabel = "Close",
  illustration,
  title,
  headingRef: headingRefProp,
  modalRef: modalRefProp,
  scrollLock = true,
  position,
  headingLevel,
  data,
  background,
  hideXButton,
  headerIllustration,
  actionButton,
  ...restProps
}) => {
  const defaultModalRef = useRef(null);
  const modalRef = modalRefProp || defaultModalRef;
  const defaultHeadingRef = useRef(null);
  const headingRef = headingRefProp || defaultHeadingRef;
  const descriptionId = `${id}_desc`;
  const handleEscape = (event) => {
    const targetKey = normalizeKey(event);
    if (targetKey === "Escape") {
      event.stopPropagation();
      onClose();
    }
  };
  const justifyContent = { left: "flexStart", center: "center", right: "flexEnd" }[position];
  const closeButtonTone = headerIllustration ? "neutral" : "secondary";
  return /* @__PURE__ */ jsx(
    Box,
    {
      role: "dialog",
      "aria-label": title,
      "aria-describedby": description ? descriptionId : void 0,
      "aria-modal": "true",
      id,
      onKeyDown: handleEscape,
      position: "relative",
      width: "full",
      height: "full",
      display: "flex",
      alignItems: "center",
      justifyContent,
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent,
          height: position === "right" || position === "left" ? "full" : void 0,
          width: width !== "content" ? "full" : void 0,
          maxWidth: width !== "content" ? width : void 0,
          children: [
            /* @__PURE__ */ jsx(RemoveScroll, { ref: modalRef, forwardProps: true, enabled: scrollLock, children: /* @__PURE__ */ jsx(
              Box,
              {
                background,
                borderRadius: position === "center" ? "xlarge" : void 0,
                overflow: "auto",
                position: "relative",
                boxShadow: "large",
                width: width !== "content" ? "full" : void 0,
                height: position === "right" || position === "left" ? "full" : void 0,
                padding: modalPadding,
                className: [pointerEventsAll, position === "center" && maxSize[position]],
                ...buildDataAttributes({ data, validateRestProps: restProps }),
                children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
                  illustration || headerIllustration ? /* @__PURE__ */ jsxs(Stack, { space: "gutter", align: "center", children: [
                    !headerIllustration && /* @__PURE__ */ jsx(Box, { paddingX: "gutter", children: illustration }),
                    /* @__PURE__ */ jsx(
                      ModalContentHeader,
                      {
                        title,
                        headingLevel,
                        description,
                        descriptionId,
                        center: Boolean(illustration),
                        ref: headingRef,
                        headerIllustration,
                        actionButton
                      }
                    )
                  ] }) : /* @__PURE__ */ jsxs(Columns, { space: "none", children: [
                    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
                      ModalContentHeader,
                      {
                        title,
                        headingLevel,
                        description,
                        descriptionId,
                        actionButton,
                        ref: headingRef
                      }
                    ) }),
                    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { width: "touchable" }) })
                  ] }),
                  /* @__PURE__ */ jsx(Box, { height: "full", children })
                ] })
              }
            ) }),
            !hideXButton && /* @__PURE__ */ jsx(
              Box,
              {
                position: "absolute",
                zIndex: "sticky",
                top: 0,
                pointerEvents: "none",
                width: "full",
                display: "flex",
                justifyContent: "flexEnd",
                paddingTop: modalPadding,
                paddingRight: modalPadding,
                className: position === "center" && maxSize[position],
                children: /* @__PURE__ */ jsx(Bleed, { space: "xsmall", children: /* @__PURE__ */ jsx(
                  Box,
                  {
                    position: "relative",
                    borderRadius: "full",
                    padding: "xsmall",
                    className: [closeIconOffset, pointerEventsAll],
                    background: headerIllustration ? "bodyDark" : void 0,
                    children: /* @__PURE__ */ jsx(
                      ButtonIcon,
                      {
                        id: `${id}-close`,
                        data: { testid: `${id}-close` },
                        label: closeLabel,
                        icon: /* @__PURE__ */ jsx(IconClose, {}),
                        tone: closeButtonTone,
                        variant: "transparent",
                        size: "large",
                        onClick: onClose
                      }
                    )
                  }
                ) })
              }
            )
          ]
        }
      )
    }
  );
};
export {
  ModalContent
};
